/**
 * 外部システム(Routine など)の確定から作った支払の明細を出す(2026-10-23・表示だけ)。申請の詳細(ApplicationDetailModal)で使う。
 *  - Routine の確定の写し(routine_closings)が読めれば、Routine の PDF と同じ形(御支払通知書・支払い明細)で出す。原本の PDF が届いていれば一緒に出す
 *  - 写しが無い・読めない(権限が無い)ときは、受け取った明細(総額 − 控除 = 差引支払額)を表で出す
 * 控除の計算は Routine で行い、Gate は受け取った明細を出すだけ
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMasters } from '@core/masters/MastersProvider';
import { formatYen } from '@core/lib/money';
import type { ExternalOrigin, PaymentDetail, RoutineClosing } from '@core/lib/types';
import { fetchRoutineClosingBySource } from './routineClosingRepo';
import RoutineStatement, { issuerFromCompanies } from './RoutineStatement';
import { RoutineDocuments } from './RoutineClosingDetailModal';

const SOURCE_LABEL: Record<ExternalOrigin['source_system'], string> = { routine: 'Routine(運送)', callbank: 'Call Bank(CC)' };

/** 申請者の欄などに出す名前(例: Routine(運送)の確定) */
export function externalOriginLabel(e: Pick<ExternalOrigin, 'source_system'> | null | undefined): string | null {
  return e ? `${SOURCE_LABEL[e.source_system] ?? e.source_system}の確定` : null;
}

export default function ExternalDetail({ origin, detail }: { origin: ExternalOrigin; detail: PaymentDetail | null }) {
  const navigate = useNavigate();
  const { companies } = useMasters();
  const [closing, setClosing] = useState<RoutineClosing | null>(null);
  const [loaded, setLoaded] = useState(origin.source_system !== 'routine');

  useEffect(() => {
    if (origin.source_system !== 'routine') return;
    let alive = true;
    fetchRoutineClosingBySource(origin.source_id)
      .then((c) => alive && setClosing(c))
      .catch(() => alive && setClosing(null)) // 読めない人(権限なし)は表で出す
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [origin.source_system, origin.source_id]);

  if (!loaded) return <p className="hint">読み込み中…</p>;

  if (closing) {
    return (
      <div>
        <RoutineStatement closing={closing} issuer={issuerFromCompanies(companies)} />
        <RoutineDocuments closing={closing} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <button className="btn-line" style={{ padding: '6px 14px', fontSize: 12.5 }} onClick={() => navigate(`/routine/print/${closing.id}`)}>
            印刷・PDF 保存
          </button>
          <span className="hint">
            {externalOriginLabel(origin)}(確定 ID: {origin.source_id})。「Routine の締め」の画面でも見られます。
          </span>
        </div>
      </div>
    );
  }

  if (!detail) return <p className="hint">{externalOriginLabel(origin)}(確定 ID: {origin.source_id})。明細はありません。</p>;
  return (
    <div>
      <table className="no-stack routine-statement" style={{ maxWidth: 520 }}>
        <tbody>
          <tr className="reward">
            <td>総額(報酬)</td>
            <td className="num">{formatYen(detail.gross)}</td>
          </tr>
          {detail.deductions.map((x, i) => (
            <tr key={`${x.label}${i}`} className="deduction">
              <td>{x.label}</td>
              <td className="num">▲{formatYen(x.amount)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>差引支払額</td>
            <td className="num">{formatYen(detail.net)}</td>
          </tr>
        </tbody>
      </table>
      <p className="hint" style={{ marginTop: 6 }}>
        {externalOriginLabel(origin)}(確定 ID: {origin.source_id})。
      </p>
    </div>
  );
}
